// App shell. AuthGate blocks rendering until we know the caller is a
// signed-in Squint team member. Then, before the main layout, we gate on
// whether the user has explicitly picked a persona — first-time visitors
// see the PersonaPicker landing so they enter the app in the right
// starting page. Subsequent visits go straight into the routed layout.
import { useState } from "react";
import { Route, Switch } from "wouter";
import { AuthGate } from "@/auth/AuthGate";
import { PersonaProvider, usePersona } from "@/auth/Persona";
import { CommandPalette, useCommandPaletteHotkey } from "@/components/CommandPalette";
import { TopBar } from "@/components/TopBar";
import { usePageViewTracker } from "@/lib/track";
import { ConfirmProvider } from "@/ui/confirm";
import { FeatureDetail } from "@/pages/FeatureDetail";
import { FlagDetail } from "@/pages/FlagDetail";
import { Analytics } from "@/pages/Analytics";
import { PersonaPicker } from "@/pages/PersonaPicker";
import { ReleaseDetail } from "@/pages/ReleaseDetail";
import { Timeline } from "@/pages/Timeline";
import { NotFound } from "@/pages/NotFound";

export function App() {
  return (
    <AuthGate>
      <PersonaProvider>
        <ConfirmProvider>
          <AppBody />
        </ConfirmProvider>
      </PersonaProvider>
    </AuthGate>
  );
}

/** Split out from App so we can consume usePersona() — the provider it
 *  reads sits above. Renders the picker on first visit; otherwise the
 *  normal routed layout. */
function AppBody() {
  const { hasPicked } = usePersona();
  const [paletteOpen, setPaletteOpen] = useState(false);
  useCommandPaletteHotkey(setPaletteOpen);

  if (!hasPicked) return <PersonaPicker />;

  return (
    <>
      <PageViewTracker />
      <TopBar onOpenPalette={() => setPaletteOpen(true)} />
      <main className="mx-auto min-w-0 max-w-6xl px-6 pb-16 pt-14">
        <Switch>
          <Route path="/" component={Timeline} />
          <Route path="/features/:id" component={FeatureDetail} />
          <Route path="/timeline" component={Timeline} />
          <Route path="/releases/:id" component={ReleaseDetail} />
          <Route path="/flags/:id" component={FlagDetail} />
          <Route path="/analytics" component={Analytics} />
          <Route component={NotFound} />
        </Switch>
      </main>
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </>
  );
}

/** Small hook-carrier — usePageViewTracker needs to sit inside both the
 *  Persona provider (for the current lens) and wouter's Router context.
 *  Rendering it as a child component keeps App declarative. */
function PageViewTracker() {
  usePageViewTracker();
  return null;
}
